import type { HTMLAttributes, PropsWithChildren } from 'react';
import type { To } from 'react-router-dom';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { mergeRefs } from 'react-merge-refs';

import type { IconName } from '../../assets/Icon/Icon';
import type { ControlContextValue } from '../../controls/shared/context';
import type { CSS } from '../../stitches.config';
import type { GutterContext } from '../../utilities/shared/Gutter';
import type {
  PolymorphicComponentProps,
  PolymorphicComponentPropsWithRef,
  PolymorphicRef,
} from '../../utilities/types/polymorphicAsProp';
import type { AccessLevelProp } from '../../utilities/useAccessLevelContext';
import type { IconTooltipProps, LabelTooltipProps } from '../Tooltip/Tooltip';
import { Icon } from '../../assets/Icon/Icon';
import { fade, palette } from '../../common/colors';
import { devices } from '../../common/devices';
import { focusRingPseudoElement, focusRingWidth } from '../../common/focus_rings';
import { sizing } from '../../common/sizing';
import { transitions, transitionTiming } from '../../common/transitions';
import { VisuallyHidden, visuallyHiddenCSS } from '../../common/visually_hidden';
import { ControlProvider } from '../../controls/shared/context';
import { selectors } from '../../controls/shared/styles';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import {
  colors,
  css,
  darkThemeSelector,
  fonts,
  globalCss,
  rem,
  shadows,
  styled,
} from '../../stitches.config';
import { BodySansSizes } from '../../text/Body';
import { SmallSansSizes } from '../../text/Small';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { AccessLevel } from '../../utilities/useAccessLevelContext';
import { SelectedProvider, useSelected } from '../../utilities/useSelected';
import { IconTooltip, LabelTooltip } from '../Tooltip/Tooltip';
import { useCanScrollX } from './useCanScrollX';

const ZINDEX_HEAD_LEADING = 20;
const ZINDEX_HEAD_STUCK = 19;
const ZINDEX_HEAD_SORTED = 18;
const ZINDEX_HEAD_INITIAL = 17;

const ZINDEX_BODY_STICKY_SELECTED = 16;
const ZINDEX_BODY_STICKY = 15;
const ZINDEX_BODY_BUFFER = 14;

const ZINDEX_BODY_FOCUSED = 12;
const ZINDEX_BODY_SELECTED = 11;
const ZINDEX_BODY_HOVERED = 5;
const ZINDEX_BODY_INITIAL = 1;
const ZINDEX_BODY_DISABLED = 0;

const CELL_COLLAPSE_WIDTH = 32;
export const CELL_SELECTOR_WIDTH = 32;
const CELL_MAX_WIDTH_STICKY = 'min(75vw, $560)';

const stateCellWidth = 8;

// A row stays hovered while a menu opened from it is open, since using the menu takes the pointer
// off the row.
const rowHover = `${selectors.hover}, &:has([data-popup-open])`;

// What a row shows only on hover fades by this one value rather than its own transition, so it all
// fades together and scrolling it in or out of its slot doesn't fade it. Registered so it transitions.
const rowReveal = '--table-row-reveal';
globalCss({
  [`@property ${rowReveal}`]: { syntax: "'<number>'", inherits: true, initialValue: '0' },
})();

type PinnedSide = 'left' | 'right';

const pinnedSides: PinnedSide[] = ['left', 'right'];
const MAX_PINNED_CELLS = 6;

const pinnedSide = (isLeading?: boolean, stuck?: 'top' | 'right' | 'bottom' | 'left') => {
  if (isLeading || stuck === 'left') {
    return 'left';
  }
  if (stuck === 'right') {
    return 'right';
  }
  return undefined;
};

type TableNestedProp = boolean;
type TableMultiRowSelectionProp = boolean;
export type TableVariantProp = 'application' | 'neutral' | 'surface';

const TableNestedContext = createContext<boolean | undefined>(undefined);
export const TableNestedProvider = TableNestedContext.Provider;

const TableVariantContext = createContext<TableVariantProp | undefined>(undefined);
export const TableVariantProvider = TableVariantContext.Provider;

const TableMultiRowSelectionContext = createContext<boolean | undefined>(undefined);
export const TableMultiRowSelectionProvider = TableMultiRowSelectionContext.Provider;

export const useTableNested = (
  controlledValue?: TableNestedProp,
  defaultValue: TableNestedProp = false,
) => {
  const tableNested = useContext(TableNestedContext);
  return controlledValue ?? tableNested ?? defaultValue;
};

export const useTableVariant = (
  controlledValue?: TableVariantProp,
  defaultValue: TableVariantProp = 'application',
) => {
  const tableVariant = useContext(TableVariantContext);
  return controlledValue ?? tableVariant ?? defaultValue;
};

export const useTableMultiRowSelection = (
  controlledValue?: TableMultiRowSelectionProp,
  defaultValue: TableMultiRowSelectionProp = false,
) => {
  const tableMultiRowSelection = useContext(TableMultiRowSelectionContext);
  return controlledValue ?? tableMultiRowSelection ?? defaultValue;
};

const tableValues = {
  touch: {
    head: '$28',
    base: '$36',
    subline: '$60',
    padding: `$6 $${stateCellWidth}`,
  },
  mouse: {
    head: '$24',
    base: '$32',
    subline: '$48',
    padding: `$4 $${stateCellWidth}`,
  },
};

const TableCellCollapsable = styled(Icon, {
  width: '$12',
  height: '$12',
});
TableCellCollapsable.displayName = 'TableCellCollapsable';

const TableCellContents = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
  color: '$$textColor',
  fontFamily: fonts.sans,
  fontWeight: '$regular',
  fontVariant: 'tabular-nums',
  verticalAlign: 'middle',
  wordBreak: 'keep-all',
  whiteSpace: 'nowrap',
  cursor: 'default',
  ...BodySansSizes,

  [devices.touch]: {
    heightAll: tableValues.touch.base,
  },

  [devices.mouse]: {
    heightAll: tableValues.mouse.base,
  },

  [selectors.focus]: {
    outline: 'none',
  },

  variants: {
    alignment: {
      start: {
        justifyContent: 'flex-start',
      },
      center: {
        justifyContent: 'center',
      },
      end: {
        justifyContent: 'flex-end',
      },
    },
    hasField: {
      true: {
        [devices.touch]: {
          padding: '$6 $6',
        },

        [devices.mouse]: {
          padding: '$4 $2',
        },
      },
      false: {
        [devices.touch]: {
          padding: tableValues.touch.padding,
        },

        [devices.mouse]: {
          padding: tableValues.mouse.padding,
        },
      },
    },
    hasNested: {
      true: {
        padding: 0,
        heightAll: 'auto',
        maxHeight: 'none',

        [selectors.focus]: {
          '&::after': {
            display: 'none',
          },
        },
      },
      false: {},
    },
    hasSubline: {
      true: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',

        [devices.touch]: {
          heightAll: tableValues.touch.subline,
        },

        [devices.mouse]: {
          heightAll: tableValues.mouse.subline,
        },
      },
      false: {},
    },
    rowHasSubline: {
      true: {
        [devices.touch]: {
          heightAll: tableValues.touch.subline,
        },
        [devices.mouse]: {
          heightAll: tableValues.mouse.subline,
        },
      },
      false: {},
    },
    head: {
      true: {
        minHeight: 'auto',
      },
      false: {},
    },
    isLeading: {
      true: {
        fontWeight: '$bold',

        [devices.mouse]: {
          minWidth: 0,
        },
      },
      false: {},
    },
    isNavigable: {
      true: {
        cursor: 'pointer',

        '&::before': focusRingPseudoElement('$4', '$8'),

        [selectors.focus]: {
          '&::before': {
            boxShadow: shadows.focusRingLight,

            [darkThemeSelector]: {
              boxShadow: shadows.focusRingDark,
            },
          },
        },
      },
      false: {},
    },
    isNested: {
      true: {},
      false: {},
    },
    internal: {
      true: {
        color: '$$internalBodyLight',

        [darkThemeSelector]: {
          color: '$$internalBodyDark',
        },
      },
      false: {},
    },
    stuck: {
      top: {},
      right: {
        [devices.mouse]: {
          minWidth: 0,
        },
      },
      bottom: {},
      left: {
        [devices.mouse]: {
          minWidth: 0,
        },
      },
    },
    variant: {
      application: {},
      neutral: {},
      surface: {},
    },
  },
  compoundVariants: [
    {
      variant: 'application',
      isLeading: true,
      css: {
        background: colors.bgApplicationLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
        },
      },
    },
    {
      variant: 'application',
      stuck: 'top',
      css: {
        background: colors.bgApplicationLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
        },
      },
    },
    {
      variant: 'application',
      stuck: 'right',
      css: {
        background: colors.bgApplicationLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
        },
      },
    },
    {
      variant: 'application',
      stuck: 'bottom',
      css: {
        background: colors.bgApplicationLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
        },
      },
    },
    {
      variant: 'application',
      stuck: 'left',
      css: {
        background: colors.bgApplicationLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
        },
      },
    },
    {
      variant: 'surface',
      isLeading: true,
      css: {
        background: colors.bgSurfaceLight,

        [darkThemeSelector]: {
          background: colors.bgSurfaceDark,
        },
      },
    },
    {
      variant: 'surface',
      stuck: 'top',
      css: {
        background: colors.bgSurfaceLight,

        [darkThemeSelector]: {
          background: colors.bgSurfaceDark,
        },
      },
    },
    {
      variant: 'surface',
      stuck: 'right',
      css: {
        background: colors.bgSurfaceLight,

        [darkThemeSelector]: {
          background: colors.bgSurfaceDark,
        },
      },
    },
    {
      variant: 'surface',
      stuck: 'bottom',
      css: {
        background: colors.bgSurfaceLight,

        [darkThemeSelector]: {
          background: colors.bgSurfaceDark,
        },
      },
    },
    {
      variant: 'surface',
      stuck: 'left',
      css: {
        background: colors.bgSurfaceLight,

        [darkThemeSelector]: {
          background: colors.bgSurfaceDark,
        },
      },
    },
    {
      variant: 'neutral',
      isLeading: true,
      css: {
        background: colors.bgNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },
      },
    },
    {
      variant: 'neutral',
      stuck: 'top',
      css: {
        background: colors.bgNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },
      },
    },
    {
      variant: 'neutral',
      stuck: 'right',
      css: {
        background: colors.bgNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },
      },
    },
    {
      variant: 'neutral',
      stuck: 'bottom',
      css: {
        background: colors.bgNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },
      },
    },
    {
      variant: 'neutral',
      stuck: 'left',
      css: {
        background: colors.bgNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },
      },
    },
    {
      isLeading: true,
      isNavigable: true,
      css: {
        color: '$$leadingTextColor',

        [selectors.hover]: {
          color: colors.linkAlternativeInteractiveLight,

          [darkThemeSelector]: {
            color: colors.linkAlternativeInteractiveDark,
          },
        },
      },
    },
  ],
});
TableCellContents.displayName = 'TableCellContents';

const TableCellSubline = styled('div', {
  ...SmallSansSizes,
  color: '$$textColor',
  opacity: 0.7,
  marginTop: '$2',
  fontFamily: fonts.sans,
  fontWeight: '$regular',
  fontVariant: 'tabular-nums',
  wordBreak: 'break-word',
  whiteSpace: 'normal',

  variants: {
    internal: {
      true: {
        color: '$$internalBodyLight',

        [darkThemeSelector]: {
          color: '$$internalBodyDark',
        },
      },
      false: {},
    },
  },
});
TableCellSubline.displayName = 'TableCellSubline';

const TableCellMainLine = styled('div', {
  width: '100%',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});
TableCellMainLine.displayName = 'TableCellMainLine';

// `clip-path` rather than `overflow: hidden` leaves a focus ring at the cell's edge room to show;
// unpinned cells grow to fit their content, so nothing else reaches past them. While focused, a
// cell rises above its neighbors' fills, short of the pinned cells.
const TableCellContainer = styled('td', {
  position: 'relative',
  zIndex: ZINDEX_BODY_INITIAL,
  padding: 0,
  clipPath: `inset(-${focusRingWidth}px)`,

  '&:not([data-pinned]):has(:focus-visible)': {
    zIndex: ZINDEX_BODY_FOCUSED,
  },

  variants: {
    collapsable: {
      true: {},
      false: {},
    },
    hasNested: {
      true: {
        padding: `0 0 $16 $${CELL_COLLAPSE_WIDTH}`,
      },
      false: {},
    },
    rowHasSubline: {
      true: {
        [devices.touch]: {
          heightAll: tableValues.touch.subline,
        },
        [devices.mouse]: {
          heightAll: tableValues.mouse.subline,
        },
      },
      false: {},
    },
    isNested: {
      true: {},
      false: {},
    },
    isLeading: {
      true: {
        [devices.mouse]: {
          position: 'sticky',
          left: '$$tableGutter',
          maxWidth: CELL_MAX_WIDTH_STICKY,
          minWidth: 0,
        },
      },
      false: {},
    },
    isSelected: {
      true: {
        zIndex: ZINDEX_BODY_SELECTED,
      },
      false: {},
    },
    stuck: {
      top: {
        [devices.mouse]: {
          position: 'sticky',
          top: 0,
        },
      },
      right: {
        [devices.mouse]: {
          position: 'sticky',
          right: '$$tableGutter',
          maxWidth: CELL_MAX_WIDTH_STICKY,
          minWidth: 0,
        },
      },
      bottom: {
        [devices.mouse]: {
          position: 'sticky',
          bottom: 0,
        },
      },
      left: {
        [devices.mouse]: {
          position: 'sticky',
          left: '$$tableGutter',
          maxWidth: CELL_MAX_WIDTH_STICKY,
          minWidth: 0,
        },
      },
    },
    isMultiRowSelection: {
      true: {},
      false: {},
    },
    hasCollapsableBefore: {
      true: {},
      false: {},
    },
  },
  compoundVariants: [
    {
      isSelected: true,
      isLeading: true,
      css: {
        zIndex: ZINDEX_BODY_STICKY_SELECTED,
      },
    },
    {
      isNested: false,
      isLeading: true,
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: false,
      stuck: 'top',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: false,
      stuck: 'right',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: false,
      stuck: 'bottom',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: false,
      stuck: 'left',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: true,
      isLeading: true,
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: true,
      stuck: 'top',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: true,
      stuck: 'right',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: true,
      stuck: 'bottom',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      isNested: true,
      stuck: 'left',
      css: {
        zIndex: ZINDEX_BODY_STICKY,
      },
    },
    {
      collapsable: true,
      stuck: 'left',
      css: {
        [devices.mouse]: {
          minWidth: `$${CELL_COLLAPSE_WIDTH}`,
        },
      },
    },
    {
      stuck: 'left',
      hasCollapsableBefore: true,
      css: {
        [devices.mouse]: {
          left: `calc($$tableGutter + $${CELL_COLLAPSE_WIDTH})`,
        },
      },
    },
    {
      isLeading: true,
      isMultiRowSelection: false,
      hasCollapsableBefore: true,
      css: {
        [devices.mouse]: {
          left: `calc($$tableGutter + $${CELL_COLLAPSE_WIDTH})`,
        },
      },
    },
    {
      isLeading: true,
      isMultiRowSelection: true,
      hasCollapsableBefore: false,
      css: {
        [devices.mouse]: {
          left: `calc($$tableGutter + $${CELL_SELECTOR_WIDTH})`,
        },
      },
    },
    {
      isLeading: true,
      isMultiRowSelection: true,
      hasCollapsableBefore: true,
      css: {
        [devices.mouse]: {
          left: `calc($$tableGutter + $${CELL_COLLAPSE_WIDTH + CELL_SELECTOR_WIDTH})`,
        },
      },
    },
  ],
});
TableCellContainer.displayName = 'TableCellContainer';

interface TableCellProps {
  /**
   * The alignment of the cell content.
   */
  alignment?: 'start' | 'center' | 'end';
  /**
   * Whether the cell should be collapsable.
   */
  collapsable?: boolean;
  /**
   * Whether the cell is collapsed.
   */
  collapsed?: boolean;
  /**
   * The number of columns the cell should span.
   */
  colSpan?: number;
  /**
   * Whether the cell has a nested `Table`.
   */
  hasNested?: boolean;
  /**
   * Whether the cell is the leading cell in the row.
   */
  isLeading?: boolean;
  /**
   * Whether the cell has a field.
   */
  hasField?: boolean;
  /**
   * Whether the cell is part of a nested `Table`.
   */
  isNested?: boolean;
  /**
   * Whether the cell is navigable and should show it can be clicked/tapped.
   */
  isNavigable?: boolean;
  /**
   * Whether the cell is stuck to the top, right, bottom, or left of the `Table`.
   */
  stuck?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Access level for the cell.
   */
  accessLevel?: AccessLevelProp | null;
  /**
   * Whether the cell is visible only to Meter employees.
   */
  internal?: boolean;
  /**
   * The group the cell belongs to.
   */
  group?: string | null;
  /**
   * Hotkey navigation targets for the cell.
   */
  hotkeyTos?: Record<string, To>;
  /**
   * Navigation target for the cell; also marks the cell navigable.
   */
  to?: To;
  /**
   * Whether there is a collapsable cell before this cell (used for sticky positioning).
   */
  hasCollapsableBefore?: boolean;
  /**
   * Subline content to display below the main content. When provided, the cell will display
   * two rows: the main content and the subline content.
   */
  subline?: React.ReactNode;
  /**
   * Whether any cell in this row has a subline. When true, this cell will match the height
   * of cells with sublines even if it doesn't have one itself.
   */
  rowHasSubline?: boolean;
  /**
   * Whether the cell content should be hidden until the row is hovered.
   */
  showOnHover?: boolean;
}

const tableCellControls: ControlContextValue = { size: 'small' };

function TableCellPassthrough<Tag extends React.ElementType>(
  {
    as,
    alignment,
    children,
    collapsed,
    collapsable,
    colSpan,
    hasField = false,
    hasNested,
    isLeading,
    isNavigable,
    isNested,
    isSelected,
    multiRowSelection,
    stuck,
    to,
    internal,
    accessLevel,
    group,
    hotkeyTos,
    hasCollapsableBefore,
    subline,
    rowHasSubline,
    showOnHover,
    style,
    ...props
  }: PolymorphicComponentProps<Tag, TableCellProps>,
  ref: PolymorphicRef<Tag>,
) {
  const tableVariant = useTableVariant();
  const tableNested = useTableNested(isNested);
  const tableMultiRowSelection = useTableMultiRowSelection(multiRowSelection);
  const tableRowSelected = useSelected(isSelected);
  const hasSubline = !!subline;
  const internalValue =
    internal ?? (accessLevel ? accessLevel === AccessLevel.Internal : undefined);

  return (
    <TableCellContainer
      collapsable={collapsable}
      colSpan={colSpan}
      hasNested={hasNested}
      isNested={tableNested}
      isMultiRowSelection={tableMultiRowSelection}
      isLeading={isLeading}
      isSelected={tableRowSelected}
      stuck={stuck}
      data-stuck={stuck}
      data-pinned={pinnedSide(isLeading, stuck)}
      data-show-on-hover={showOnHover || undefined}
      hasCollapsableBefore={hasCollapsableBefore}
      rowHasSubline={rowHasSubline}
      style={style}
    >
      <TableCellContents
        as={collapsable ? 'button' : as}
        to={to}
        {...(as && typeof as !== 'string' ? { hotkeyTos } : {})}
        alignment={alignment}
        hasField={hasField}
        hasNested={hasNested}
        hasSubline={hasSubline}
        rowHasSubline={rowHasSubline}
        isLeading={isLeading}
        isNavigable={!!to || isNavigable || collapsable}
        isNested={tableNested}
        stuck={stuck}
        tabIndex={collapsable || (isLeading && to) ? 0 : undefined}
        aria-expanded={collapsable ? !collapsed : undefined}
        data-has-nested={hasNested && 'true'}
        {...props}
        internal={internalValue}
        variant={tableVariant}
        ref={ref}
      >
        {collapsable && (
          <>
            <TableCellCollapsable icon={collapsed ? 'chevron-right' : 'chevron-down'} />
            {/* The chevron is this cell's only content, so nothing else names the button. Static
                text, not expand/collapse: `aria-expanded` already carries the state. */}
            <VisuallyHidden>Toggle row</VisuallyHidden>
          </>
        )}
        <ControlProvider value={tableCellControls}>
          {hasSubline ? (
            <>
              <TableCellMainLine>{children}</TableCellMainLine>
              <TableCellSubline internal={internalValue}>{subline}</TableCellSubline>
            </>
          ) : (
            children
          )}
        </ControlProvider>
      </TableCellContents>
    </TableCellContainer>
  );
}

export const TableCell = React.forwardRef(TableCellPassthrough) as <
  Tag extends React.ElementType = 'div',
>(
  props: PolymorphicComponentPropsWithRef<Tag, TableCellProps>,
) => ReturnType<typeof TableCellPassthrough>;

// The gutter only holds its place as the end of a pinned column beside it; anywhere else it
// scrolls away with the rest of the row. A pinned column further in only claims the leading gutter
// once it has stuck, which `useCanScrollX` reports. (Stitches splits selectors on every comma, so
// these stay separate rather than inside an `:is()`.)
const besidePinned = {
  leading: ':has(> [data-side="leading"] + [data-pinned="left"]) > &[data-side="leading"]',
  trailing: ':has(> [data-pinned="right"]) > &[data-side="trailing"]',
};
const claimedByPinned =
  '[data-leading-gutter-stuck="true"] > * > :has(> [data-pinned="left"]) > &[data-side="leading"]';
const stuckGutter = {
  leading: `${besidePinned.leading}, ${claimedByPinned}`,
  trailing: besidePinned.trailing,
};
const stuckGutterScrolled = `[data-can-scroll-x="true"] ${besidePinned.leading}, [data-can-scroll-x="true"] ${besidePinned.trailing}, ${claimedByPinned}`;

// A stuck gutter needs a fill to hide what scrolls beneath it.
const gutterScrolledBackground = {
  application: {
    [stuckGutterScrolled]: {
      background: colors.bgApplicationLight,

      [darkThemeSelector]: {
        background: colors.bgApplicationDark,
      },
    },
  },
  neutral: {
    [stuckGutterScrolled]: {
      background: colors.bgNeutralLight,

      [darkThemeSelector]: {
        background: colors.bgNeutralDark,
      },
    },
  },
  surface: {
    [stuckGutterScrolled]: {
      background: colors.bgSurfaceLight,

      [darkThemeSelector]: {
        background: colors.bgSurfaceDark,
      },
    },
  },
};

// The head's top hairline and the last row's bottom one hang half a pixel past the table, under the
// gutter. Table cells can't take a box-shadow under `border-collapse`, and a full pixel covers it
// cleanly at 1x.
const hairlineCover: CSS = {
  content: '""',
  position: 'absolute',
  left: 0,
  right: 0,
  height: '1px',
  background: 'inherit',
  pointerEvents: 'none',
};

const gutterHairlineCovers = {
  [stuckGutterScrolled]: {
    '&:is(thead > :first-child > *)::before': { ...hairlineCover, bottom: '100%' },
    '&:is(tbody > :last-child > *)::after': { ...hairlineCover, top: '100%' },
  },
};

export const TableCellBufferContainer = styled('td', {
  widthAll: `calc($$tableGutter - $${stateCellWidth})`,

  [devices.mouse]: {
    zIndex: ZINDEX_BODY_BUFFER,

    [stuckGutter.leading]: {
      position: 'sticky',
      left: 0,
    },

    [stuckGutter.trailing]: {
      position: 'sticky',
      right: 0,
    },

    ...gutterHairlineCovers,
  },

  variants: {
    head: {
      true: {
        position: 'sticky',
        top: 0,
        zIndex: ZINDEX_HEAD_STUCK,

        [devices.touch]: {
          heightAll: tableValues.touch.head,
        },

        [devices.mouse]: {
          heightAll: tableValues.mouse.head,
        },
      },
      false: {},
    },
    isNested: {
      true: {},
      false: {},
    },
    side: {
      leading: {},
      trailing: {},
    },
    variant: gutterScrolledBackground,
  },

  compoundVariants: [
    {
      head: true,
      variant: 'application',
      side: 'leading',
      css: {
        background: `linear-gradient(to left, ${colors.bgApplicationLight}, transparent 90%, transparent)`,

        [darkThemeSelector]: {
          background: `linear-gradient(to left, ${colors.bgApplicationDark}, transparent 90%, transparent)`,
        },
      },
    },
    {
      head: true,
      variant: 'application',
      side: 'trailing',
      css: {
        background: `linear-gradient(to right, ${colors.bgApplicationLight}, transparent 90%, transparent)`,

        [darkThemeSelector]: {
          background: `linear-gradient(to right, ${colors.bgApplicationDark}, transparent 90%, transparent)`,
        },
      },
    },
    {
      head: true,
      variant: 'surface',
      side: 'leading',
      css: {
        background: `linear-gradient(to left, ${colors.bgSurfaceLight}, transparent 90%, transparent)`,

        [darkThemeSelector]: {
          background: `linear-gradient(to left, ${colors.bgSurfaceDark}, transparent 90%, transparent)`,
        },
      },
    },
    {
      head: true,
      variant: 'surface',
      side: 'trailing',
      css: {
        background: `linear-gradient(to right, ${colors.bgSurfaceLight}, transparent 90%, transparent)`,

        [darkThemeSelector]: {
          background: `linear-gradient(to right, ${colors.bgSurfaceDark}, transparent 90%, transparent)`,
        },
      },
    },
    {
      head: true,
      variant: 'neutral',
      side: 'leading',
      css: {
        background: `linear-gradient(to left, ${colors.bgNeutralLight}, transparent 90%, transparent)`,

        [darkThemeSelector]: {
          background: `linear-gradient(to left, ${colors.bgNeutralDark}, transparent 90%, transparent)`,
        },
      },
    },
    {
      head: true,
      variant: 'neutral',
      side: 'trailing',
      css: {
        background: `linear-gradient(to right, ${colors.bgNeutralLight}, transparent 90%, transparent)`,

        [darkThemeSelector]: {
          background: `linear-gradient(to right, ${colors.bgNeutralDark}, transparent 90%, transparent)`,
        },
      },
    },
    {
      head: true,
      isNested: true,
      side: 'leading',
      css: {
        strokeTop: colors.strokeNeutralLight,

        [darkThemeSelector]: {
          strokeTop: colors.strokeNeutralDark,
        },
      },
    },
  ],
});
TableCellBufferContainer.displayName = 'TableCellBufferContainer';

type TableCellBufferProps = {
  /**
   * Whether the cell state is at the top of the table.
   */
  head?: boolean;
  /**
   * Whether the cell is part of a nested `Table`.
   */
  isNested?: boolean;
  /**
   * The side of row the cell appears.
   */
  side: 'leading' | 'trailing';
};

export function TableCellBuffer({ head, isNested, side, ...remaining }: TableCellBufferProps) {
  const tableNested = useTableNested(isNested);
  const tableVariant = useTableVariant();
  return (
    <TableCellBufferContainer
      head={head}
      isNested={tableNested}
      side={side}
      data-side={side}
      variant={tableVariant}
      {...remaining}
    />
  );
}

const TableCellStateContents = styled('div', {
  width: '100%',

  [devices.touch]: {
    heightAll: tableValues.touch.base,
  },

  [devices.mouse]: {
    heightAll: tableValues.mouse.base,
  },

  variants: {
    head: {
      true: {
        [devices.touch]: {
          heightAll: tableValues.touch.head,
        },

        [devices.mouse]: {
          heightAll: tableValues.mouse.head,
        },
      },
      false: {},
    },
    isNested: {
      true: {},
      false: {},
    },
    rowHasSubline: {
      true: {
        [devices.touch]: {
          heightAll: tableValues.touch.subline,
        },
        [devices.mouse]: {
          heightAll: tableValues.mouse.subline,
        },
      },
      false: {},
    },
    side: {
      leading: {
        borderRadius: '$10 0 0 $10',
      },
      trailing: {
        borderRadius: '0 $10 $10 0',
      },
    },
  },
  compoundVariants: [
    {
      head: true,
      css: {
        borderRadius: 0,
      },
    },
    {
      head: true,
      isNested: true,
      side: 'leading',
      css: {
        strokeTop: colors.strokeNeutralLight,

        [darkThemeSelector]: {
          strokeTop: colors.strokeNeutralDark,
        },
      },
    },
  ],
});
TableCellStateContents.displayName = 'TableCellStateContents';

const TableCellStateContainer = styled('td', {
  padding: 0,
  width: `$${stateCellWidth}`,
  minWidth: `$${stateCellWidth}`,
  maxWidth: `$${stateCellWidth}`,

  [devices.mouse]: {
    zIndex: ZINDEX_BODY_STICKY,

    [stuckGutter.leading]: {
      position: 'sticky',
      left: `calc($$tableGutter - $${stateCellWidth})`,
    },

    [stuckGutter.trailing]: {
      position: 'sticky',
      right: `calc($$tableGutter - $${stateCellWidth})`,
    },

    ...gutterHairlineCovers,
  },

  variants: {
    head: {
      true: {
        position: 'sticky',
        top: 0,
        zIndex: ZINDEX_HEAD_STUCK,
      },
      false: {},
    },
    side: {
      leading: {},
      trailing: {},
    },
    variant: gutterScrolledBackground,
  },
  compoundVariants: [
    {
      head: true,
      variant: 'application',
      css: {
        background: colors.bgApplicationLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
        },
      },
    },
    {
      head: true,
      variant: 'surface',
      css: {
        background: colors.bgSurfaceLight,

        [darkThemeSelector]: {
          background: colors.bgSurfaceDark,
        },
      },
    },
    {
      head: true,
      variant: 'neutral',
      css: {
        background: colors.bgNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },
      },
    },
  ],
});
TableCellStateContainer.displayName = 'TableCellStateContainer';

type TableCellStateProps = {
  /**
   * Whether the cell state is at the top of the table.
   */
  head?: boolean;
  /**
   * Whether the cell is part of a nested `Table`.
   */
  isNested?: boolean;
  /**
   * The side of row the cell appears.
   */
  side: 'leading' | 'trailing';
  /**
   * Whether any cell in this row has a subline. When true, this cell will match the height
   * of cells with sublines even if it doesn't have one itself.
   */
  rowHasSubline?: boolean;
};

export function TableCellState<Tag extends React.ElementType>({
  head,
  isNested,
  side,
  rowHasSubline,
  ...remaining
}: PolymorphicComponentProps<Tag, TableCellStateProps>) {
  const tableVariant = useTableVariant();
  const tableNested = useTableNested(isNested);
  return (
    <TableCellStateContainer
      head={head}
      side={side}
      data-side={side}
      variant={tableVariant}
      {...remaining}
    >
      <TableCellStateContents
        head={head}
        isNested={tableNested}
        side={side}
        rowHasSubline={rowHasSubline}
      />
    </TableCellStateContainer>
  );
}

// Edge cells hand their outer padding to the state cell so their content lines up with the
// gutter. `clip-path` stands in for `overflow: hidden` so their focus rings can reach into it,
// and they rise above the gutter whenever nothing is scrolled beneath it on their side. A pinned
// cell's content can outgrow its max width, so its inner side still clips flush.
const edgeCellCSS = (cell: string, contents: string, zIndex: number): Record<string, CSS> => {
  const leading = `& > ${TableCellStateContainer}[data-side="leading"] + ${cell}`;
  const trailingHas = `:has(+ ${TableCellStateContainer}[data-side="trailing"])`;
  const ring = `${focusRingWidth}px`;
  const spill = rem(stateCellWidth + focusRingWidth);
  return {
    [leading]: {
      overflow: 'visible',
      clipPath: 'none',

      '[data-scrolled-x-start="true"] &': {
        zIndex,
      },

      [`& > ${contents}`]: {
        paddingLeft: 0,
        overflow: 'visible',
        clipPath: `inset(-${ring} 0 -${ring} -${spill})`,

        '&::before': {
          left: '-$4',
        },
      },
    },
    [`${leading}:not([data-pinned]) > ${contents}`]: {
      clipPath: `inset(-${ring} -${ring} -${ring} -${spill})`,
    },
    [`& > ${cell}${trailingHas}`]: {
      overflow: 'visible',
      clipPath: 'none',

      '[data-scrolled-x-end="true"] &, &[data-stuck="right"]': {
        zIndex,
      },

      [`& > ${contents}`]: {
        paddingRight: 0,
        overflow: 'visible',
        clipPath: `inset(-${ring} -${spill} -${ring} 0)`,

        '&::before': {
          right: '-$4',
        },
      },
    },
    [`& > ${cell}${trailingHas}:not([data-pinned]) > ${contents}`]: {
      clipPath: `inset(-${ring} -${spill} -${ring} -${ring})`,
    },
    [`${leading}${trailingHas} > ${contents}`]: {
      clipPath: `inset(-${ring} -${spill})`,
    },
  };
};

const pinnedShadowCSS = (side: PinnedSide): CSS => {
  const toward = side === 'left' ? 'right' : 'left';
  return {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    ...(side === 'left' ? { left: '100%' } : { right: '100%' }),
    width: '$8',
    opacity: 0,
    pointerEvents: 'none',
    transition: transitions.opacity,
    background: `linear-gradient(to ${toward}, ${fade(palette.gray700, 0.08)}, transparent)`,

    [darkThemeSelector]: {
      background: `linear-gradient(to ${toward}, ${fade(palette.black, 0.24)}, transparent)`,
    },
  };
};

// Pinned cells clip their contents rather than themselves, leaving them free to cast a shadow
// over whatever is scrolled beneath. The shadow is always there while the table can scroll so it
// can fade in; `useCanScrollX` picks which one shows on each side, by position.
const pinnedCellCSS = (contents: string): Record<string, CSS> => ({
  '& > [data-pinned]': {
    overflow: 'visible',
    clipPath: 'none',

    [`& > ${contents}`]: {
      overflowX: 'clip',
    },
  },
  [devices.mouse]: Object.fromEntries(
    pinnedSides.map((side) => [
      `[data-can-scroll-x="true"] > * > & > [data-pinned="${side}"]::after`,
      pinnedShadowCSS(side),
    ]),
  ),
  ...Object.fromEntries(
    pinnedSides.flatMap((side) =>
      Array.from({ length: MAX_PINNED_CELLS }, (_, index): [string, CSS] => [
        `[data-pinned-shadow-${side}="${index + 1}"] > * > & > :nth-child(${index + 1} of [data-pinned="${side}"]):not([data-blank])::after`,
        { opacity: 1 },
      ]),
    ),
  ),
});

const TableRowStyles = css({
  $$iconColor: colors.iconNeutralLight,
  $$textColor: colors.bodyNeutralLight,
  $$leadingTextColor: colors.linkAlternativeInitialLight,

  $$internalBodyLight: colors.internalBodyLight,
  $$internalBodyDark: colors.internalBodyDark,

  position: 'relative',
  width: '100%',

  [rowReveal]: '0',
  transition: `${rowReveal} ${transitionTiming}`,

  [darkThemeSelector]: {
    $$iconColor: colors.iconNeutralDark,
    $$textColor: colors.bodyNeutralDark,
    $$leadingTextColor: colors.linkAlternativeInitialDark,
  },

  // Each row draws only the inner half of a hairline it shares, so no stroke hangs over its
  // neighbors or the gutter. The last row has no neighbor to draw the outer half of its bottom one.
  [`& > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
    $$tableCellStroke: colors.strokeNeutralLight,
    boxShadow: `inset 0 0.5px $$tableCellStroke, inset 0 -0.5px $$tableCellStroke`,

    [darkThemeSelector]: {
      $$tableCellStroke: colors.strokeNeutralDark,
    },
  },

  [`&:last-child > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
    boxShadow: `inset 0 0.5px $$tableCellStroke, inset 0 -0.5px $$tableCellStroke, 0 0.5px $$tableCellStroke`,
  },

  // A cell rendered `as` a `Link` brings its transition, which would fade its fill and hairlines
  // behind the rest of the row's.
  [`& > ${TableCellContainer} > ${TableCellContents}`]: {
    transition: 'none',
  },

  ...edgeCellCSS(
    TableCellContainer.toString(),
    TableCellContents.toString(),
    ZINDEX_BODY_STICKY_SELECTED,
  ),
  ...pinnedCellCSS(TableCellContents.toString()),

  // A cell shown on hover hides while stuck, with the gutter beside it so the row runs on to the
  // table's edge; resting in its own slot at the end, it shows like any other.
  '[data-scrolled-x-end="false"] > * > &': {
    [devices.mouse]: {
      '& > [data-show-on-hover]': {
        opacity: `var(${rowReveal})`,
      },
      '&:has(> [data-show-on-hover][data-pinned="right"] + [data-side="trailing"]) > [data-side="trailing"]':
        {
          opacity: `var(${rowReveal})`,
        },
    },
  },

  [rowHover]: {
    [rowReveal]: '1',
  },
});

const selectedRowHoverCSS = {
  zIndex: ZINDEX_BODY_SELECTED,

  [`& > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
    zIndex: ZINDEX_BODY_SELECTED,
    $$leadingTextColor: colors.headingBrandLight,
    background: colors.bgBrandLight,
    $$tableCellStroke: colors.strokeBrandLight,

    [darkThemeSelector]: {
      $$leadingTextColor: colors.headingBrandDark,
      background: colors.bgBrandDark,
      $$tableCellStroke: colors.strokeBrandDark,
    },
  },

  [`& > ${TableCellStateContainer} > ${TableCellStateContents}`]: {
    zIndex: ZINDEX_BODY_SELECTED,
    background: colors.bgBrandLight,

    [darkThemeSelector]: {
      background: colors.bgBrandDark,
    },
  },

  [`& > ${TableCellStateContainer}[data-side="leading"] > ${TableCellStateContents}`]: {
    zIndex: ZINDEX_BODY_SELECTED,
    strokeNoRight: colors.strokeBrandLight,

    [darkThemeSelector]: {
      strokeNoRight: colors.strokeBrandDark,
    },
  },

  [`& > ${TableCellStateContainer}[data-side="trailing"] > ${TableCellStateContents}`]: {
    zIndex: ZINDEX_BODY_SELECTED,
    strokeNoLeft: colors.strokeBrandLight,

    [darkThemeSelector]: {
      strokeNoLeft: colors.strokeBrandDark,
    },
  },
};

const TableRowContainer = styled('tr', TableRowStyles, {
  position: 'relative',
  zIndex: ZINDEX_BODY_INITIAL,

  [rowHover]: {
    zIndex: ZINDEX_BODY_HOVERED,
  },

  // A focus ring can reach past its row, so lift it above its neighbors' fills.
  '&:has(:focus-visible)': {
    zIndex: ZINDEX_BODY_FOCUSED,
  },

  variants: {
    isDisabled: {
      true: {
        zIndex: ZINDEX_BODY_DISABLED,
        $$iconColor: colors.bodyNeutralLight,
        $$textColor: colors.bodyNeutralLight,
        $$leadingTextColor: colors.bodyNeutralLight,

        opacity: 0.5,

        [darkThemeSelector]: {
          $$iconColor: colors.bodyNeutralDark,
          $$textColor: colors.bodyNeutralDark,
          $$leadingTextColor: colors.bodyNeutralDark,
        },

        [`& > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
          background: colors.bgApplicationLight,
          $$tableCellStroke: colors.strokeNeutralLight,

          [darkThemeSelector]: {
            background: colors.bgApplicationDark,
            $$tableCellStroke: colors.strokeNeutralDark,
          },
        },

        [`& > ${TableCellStateContainer} > ${TableCellStateContents}`]: {
          background: colors.bgApplicationLight,

          [darkThemeSelector]: {
            background: colors.bgApplicationDark,
          },
        },

        [`& > ${TableCellStateContainer}[data-side="leading"] > ${TableCellStateContents}`]: {
          strokeNoRight: colors.bgApplicationLight,

          [darkThemeSelector]: {
            strokeNoRight: colors.bgApplicationDark,
          },
        },

        [`& > ${TableCellStateContainer}[data-side="trailing"] > ${TableCellStateContents}`]: {
          strokeNoLeft: colors.bgApplicationLight,

          [darkThemeSelector]: {
            strokeNoLeft: colors.bgApplicationDark,
          },
        },

        [rowHover]: {
          zIndex: ZINDEX_BODY_DISABLED,

          [`& > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
            zIndex: ZINDEX_BODY_DISABLED,
            $$leadingTextColor: colors.bodyNeutralLight,
            background: colors.bgNeutralLight,
            $$tableCellStroke: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              $$leadingTextColor: colors.bodyNeutralDark,
              background: colors.bgNeutralDark,
              $$tableCellStroke: colors.strokeNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer} > ${TableCellStateContents}`]: {
            zIndex: ZINDEX_BODY_DISABLED,
            background: colors.bgNeutralLight,

            [darkThemeSelector]: {
              background: colors.bgNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="leading"] > ${TableCellStateContents}`]: {
            zIndex: ZINDEX_BODY_DISABLED,
            strokeNoRight: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              strokeNoRight: colors.strokeNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="trailing"] > ${TableCellStateContents}`]: {
            zIndex: ZINDEX_BODY_DISABLED,
            strokeNoLeft: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              strokeNoLeft: colors.strokeNeutralDark,
            },
          },
        },
      },
    },
    isDragging: {
      true: {
        opacity: 0.8,
      },
      false: {},
    },
    isSelected: {
      true: {
        zIndex: ZINDEX_BODY_SELECTED,
        $$iconColor: colors.bodyBrandLight,
        $$textColor: colors.headingBrandLight,
        $$leadingTextColor: colors.headingBrandLight,

        [darkThemeSelector]: {
          $$iconColor: colors.bodyBrandDark,
          $$textColor: colors.headingBrandDark,
          $$leadingTextColor: colors.headingBrandDark,
        },

        [`& > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
          background: colors.bgBrandLight,
          $$tableCellStroke: colors.strokeBrandLight,

          [darkThemeSelector]: {
            background: colors.bgBrandDark,
            $$tableCellStroke: colors.strokeBrandDark,
          },
        },

        [`& > ${TableCellStateContainer} > ${TableCellStateContents}`]: {
          background: colors.bgBrandLight,

          [darkThemeSelector]: {
            background: colors.bgBrandDark,
          },
        },

        [`& > ${TableCellStateContainer}[data-side="leading"] > ${TableCellStateContents}`]: {
          strokeNoRight: colors.strokeBrandLight,

          [darkThemeSelector]: {
            strokeNoRight: colors.strokeBrandDark,
          },
        },

        [`& > ${TableCellStateContainer}[data-side="trailing"] > ${TableCellStateContents}`]: {
          strokeNoLeft: colors.strokeBrandLight,

          [darkThemeSelector]: {
            strokeNoLeft: colors.strokeBrandDark,
          },
        },

        [rowHover]: selectedRowHoverCSS,
      },
      false: {},
    },
    variant: {
      application: {
        [rowHover]: {
          [`& > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
            background: colors.bgNeutralLight,
            $$tableCellStroke: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              background: colors.bgNeutralDark,
              $$tableCellStroke: colors.strokeNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer} > ${TableCellStateContents}`]: {
            background: colors.bgNeutralLight,

            [darkThemeSelector]: {
              background: colors.bgNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="leading"] > ${TableCellStateContents}`]: {
            strokeNoRight: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              strokeNoRight: colors.strokeNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="trailing"] > ${TableCellStateContents}`]: {
            strokeNoLeft: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              strokeNoLeft: colors.strokeNeutralDark,
            },
          },
        },
      },
      surface: {
        [rowHover]: {
          [`& > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
            background: colors.bgSurfaceLight,
            $$tableCellStroke: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              background: colors.bgSurfaceDark,
              $$tableCellStroke: colors.strokeNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer} > ${TableCellStateContents}`]: {
            background: colors.bgSurfaceLight,

            [darkThemeSelector]: {
              background: colors.bgSurfaceDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="leading"] > ${TableCellStateContents}`]: {
            strokeNoRight: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              strokeNoRight: colors.strokeNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="trailing"] > ${TableCellStateContents}`]: {
            strokeNoLeft: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              strokeNoLeft: colors.strokeNeutralDark,
            },
          },
        },
      },
      neutral: {
        [rowHover]: {
          [`& > ${TableCellContainer} > ${TableCellContents}:not([data-has-nested="true"])`]: {
            background: colors.bgApplicationLight,
            $$tableCellStroke: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              background: colors.bgApplicationDark,
              $$tableCellStroke: colors.strokeNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer} > ${TableCellStateContents}`]: {
            background: colors.bgApplicationLight,

            [darkThemeSelector]: {
              background: colors.bgApplicationDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="leading"] > ${TableCellStateContents}`]: {
            strokeNoRight: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              strokeNoRight: colors.strokeNeutralDark,
            },
          },

          [`& > ${TableCellStateContainer}[data-side="trailing"] > ${TableCellStateContents}`]: {
            strokeNoLeft: colors.strokeNeutralLight,

            [darkThemeSelector]: {
              strokeNoLeft: colors.strokeNeutralDark,
            },
          },
        },
      },
    },
  },
  // Otherwise the hover fill from `variant`, applied after `isSelected`, wins out.
  compoundVariants: [
    {
      isSelected: true,
      variant: 'application',
      css: { [rowHover]: selectedRowHoverCSS },
    },
    {
      isSelected: true,
      variant: 'surface',
      css: { [rowHover]: selectedRowHoverCSS },
    },
    {
      isSelected: true,
      variant: 'neutral',
      css: { [rowHover]: selectedRowHoverCSS },
    },
  ],
});
TableRowContainer.displayName = 'TableRowContainer';

interface TableRowProps {
  /**
   * The content of the row.
   */
  children?: React.ReactNode | undefined;
  /**
   * Whether the row is disabled.
   */
  isDisabled?: boolean;
  /**
   * Whether the row is being dragged.
   */
  isDragging?: boolean;
  /**
   * Whether the row shows a hover state. Defaults to true.
   */
  isHoverable?: boolean;
  /**
   * Whether the row is navigable and should show it can be clicked/tapped.
   */
  isNavigable?: boolean;
  /**
   * Whether the row is part of a nested `Table`.
   */
  isNested?: boolean;
  /**
   * Whether the row is selected.
   */
  isSelected?: boolean;
}

function TableRowPassthrough<Tag extends React.ElementType>(
  {
    isDragging,
    isHoverable,
    isNested,
    isSelected,
    ...props
  }: PolymorphicComponentProps<Tag, TableRowProps>,
  ref: PolymorphicRef<Tag>,
) {
  const tableVariant = useTableVariant();
  return (
    <TableRowContainer
      ref={ref}
      {...props}
      isDragging={isDragging}
      isSelected={isSelected}
      variant={isHoverable !== false ? tableVariant : undefined}
      data-is-selected={isSelected && 'true'}
    >
      <SelectedProvider value={Boolean(isSelected)}>{props.children}</SelectedProvider>
    </TableRowContainer>
  );
}

export const TableRow = React.forwardRef(TableRowPassthrough) as <
  Tag extends React.ElementType = 'tr',
>(
  props: PolymorphicComponentPropsWithRef<Tag, TableRowProps>,
) => ReturnType<typeof TableRowPassthrough>;

export const TableBody = styled('tbody');
TableBody.displayName = 'TableBody';

const TableHeadCellSort = styled('div', {
  display: 'flex',
  alignItems: 'center',
  paddingLeft: '$2',

  variants: {
    isVisible: {
      true: {
        // TRICKY: Toggle visibility rather than display to always reserve
        // space in the layout for the icon.
        visibility: 'visible',
      },
      false: {
        visibility: 'hidden',
      },
    },
  },
  defaultVariants: {
    isVisible: false,
  },
});
TableHeadCellSort.displayName = 'TableHeadCellSort';

const TableHeadCellIcon = styled(Icon, {
  size: '$12',
  color: colors.iconNeutralLight,

  [darkThemeSelector]: {
    color: colors.iconNeutralDark,
  },
});
TableHeadCellIcon.displayName = 'TableHeadCellIcon';

const TableHeadCellLabel = styled(Text, {
  variants: {
    visible: {
      true: {},
      false: {
        ...visuallyHiddenCSS,
      },
    },
  },
});
TableHeadCellLabel.displayName = 'TableHeadCellLabel';

// Holds an icon and the label it stands in for. `display: contents` so the pair lays out exactly as
// the lone icon used to; it is an element rather than a fragment so a tooltip can still wrap it.
const TableHeadCellIconLabel = styled('div', {
  display: 'contents',
});
TableHeadCellIconLabel.displayName = 'TableHeadCellIconLabel';

const TableHeadCellSortIcon = styled(Icon, {
  display: 'flex',
  size: '$8',
});
TableHeadCellSortIcon.displayName = 'TableHeadCellSortIcon';

const TableHeadCellBadge = styled('div', {
  marginLeft: '$4',
});
TableHeadCellBadge.displayName = 'TableHeadCellBadge';

// Hang the sort icon in the padding so the label lines up with the values beneath it.
const hangingSortCSS = {
  [`& ${TableHeadCellSort}`]: {
    marginRight: '-$10',
  },
};

const TableHeadCellContents = styled('div', {
  ...SmallSansSizes,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  // Like a body row, the head draws only the inner half of the hairline it shares with the first.
  $$tableCellStroke: colors.strokeNeutralLight,
  boxShadow: `0 -0.5px $$tableCellStroke, inset 0 0.5px $$tableCellStroke, inset 0 -0.5px $$tableCellStroke`,
  cursor: 'pointer',
  fontWeight: '$bold',
  fontFamily: fonts.sans,
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',

  [darkThemeSelector]: {
    $$tableCellStroke: colors.strokeNeutralDark,
  },

  [devices.touch]: {
    heightAll: tableValues.touch.head,
    padding: tableValues.touch.padding,
  },

  [devices.mouse]: {
    heightAll: tableValues.mouse.head,
    padding: tableValues.mouse.padding,
  },

  variants: {
    alignment: {
      start: {
        justifyContent: 'flex-start',
      },
      center: {
        justifyContent: 'center',
        ...hangingSortCSS,
      },
      end: {
        justifyContent: 'flex-end',
        ...hangingSortCSS,
      },
    },
    canSort: {
      true: {
        outline: 'none',
        cursor: 'pointer',

        // No `position: relative` here on purpose: the ring lays out against the cell, not the
        // contents, and adding one would move it.
        '&::before': focusRingPseudoElement('$4', '$8'),

        [selectors.focus]: {
          '&::before': {
            boxShadow: shadows.focusRingLight,

            [darkThemeSelector]: {
              boxShadow: shadows.focusRingDark,
            },
          },
        },
      },
      false: {
        cursor: 'default',
      },
    },
    collapsable: {
      true: {
        widthAll: '100%',
      },
      false: {},
    },
    internal: {
      true: {
        color: colors.internalBodyLight,

        [darkThemeSelector]: {
          color: colors.internalBodyDark,
        },
      },
      false: {},
    },
    variant: {
      application: {
        backgroundColor: colors.bgApplicationLight,

        [darkThemeSelector]: {
          backgroundColor: colors.bgApplicationDark,
        },
      },
      surface: {
        backgroundColor: colors.bgSurfaceLight,

        [darkThemeSelector]: {
          backgroundColor: colors.bgSurfaceDark,
        },
      },
      neutral: {
        backgroundColor: colors.bgNeutralLight,

        [darkThemeSelector]: {
          backgroundColor: colors.bgNeutralDark,
        },
      },
    },
  },
});
TableHeadCellContents.displayName = 'TableHeadCellContents';

const TableHeadCellContainer = styled('th', {
  position: 'sticky',
  top: 0,
  zIndex: ZINDEX_HEAD_INITIAL,
  padding: 0,
  userSelect: 'none',

  variants: {
    collapsable: {
      true: {
        width: `$${CELL_COLLAPSE_WIDTH}`,
      },
      false: {},
    },
    isLeading: {
      true: {
        [devices.mouse]: {
          left: '$$tableGutter',
          zIndex: ZINDEX_HEAD_LEADING,
          maxWidth: CELL_MAX_WIDTH_STICKY,
          minWidth: 0,
          overflow: 'hidden',
        },
      },
      false: {},
    },
    isMultiRowSelection: {
      true: {},
      false: {},
    },
    hasCollapsableBefore: {
      true: {},
      false: {},
    },
    isSorted: {
      true: {
        zIndex: ZINDEX_HEAD_SORTED,
      },
      false: {},
    },
    stuck: {
      top: {
        [devices.mouse]: {
          zIndex: ZINDEX_HEAD_STUCK,
        },
      },
      right: {
        [devices.mouse]: {
          zIndex: ZINDEX_HEAD_STUCK,
          right: '$$tableGutter',
          maxWidth: CELL_MAX_WIDTH_STICKY,
          minWidth: 0,
          overflow: 'hidden',
        },
      },
      bottom: {
        [devices.mouse]: {
          zIndex: ZINDEX_HEAD_STUCK,
          bottom: 0,
        },
      },
      left: {
        [devices.mouse]: {
          zIndex: ZINDEX_HEAD_STUCK,
          left: '$$tableGutter',
          maxWidth: CELL_MAX_WIDTH_STICKY,
          minWidth: 0,
          overflow: 'hidden',
        },
      },
    },
  },
  compoundVariants: [
    {
      collapsable: true,
      stuck: 'left',
      css: {
        [devices.mouse]: {
          minWidth: `$${CELL_COLLAPSE_WIDTH}`,
        },
      },
    },
    {
      stuck: 'left',
      hasCollapsableBefore: true,
      css: {
        [devices.mouse]: {
          left: `calc($$tableGutter + $${CELL_COLLAPSE_WIDTH})`,
        },
      },
    },
    {
      isMultiRowSelection: false,
      isLeading: true,
      hasCollapsableBefore: true,
      css: {
        [devices.mouse]: {
          left: `calc($$tableGutter + $${CELL_COLLAPSE_WIDTH})`,
        },
      },
    },
    {
      isMultiRowSelection: true,
      isLeading: true,
      hasCollapsableBefore: false,
      css: {
        [devices.mouse]: {
          left: `calc($$tableGutter + $${CELL_SELECTOR_WIDTH})`,
        },
      },
    },
    {
      isMultiRowSelection: true,
      isLeading: true,
      hasCollapsableBefore: true,
      css: {
        [devices.mouse]: {
          left: `calc($$tableGutter + $${CELL_COLLAPSE_WIDTH + CELL_SELECTOR_WIDTH})`,
        },
      },
    },
  ],
});
TableHeadCellContainer.displayName = 'TableHeadCellContainer';

export type TableHeadCellTooltipProp = { label?: LabelTooltipProps; icon?: IconTooltipProps };

type TableHeadCellProps = PropsWithChildren<
  HTMLAttributes<HTMLTableCellElement> & {
    /**
     * The alignment of the cell content.
     */
    alignment?: 'start' | 'center' | 'end';
    /**
     * Whether the cell is collapsable.
     */
    collapsable?: boolean;
    /**
     * Whether the cell is collapsed.
     */
    collapsed?: boolean;
    /**
     * Whether to hide the label.
     */
    hideLabel?: boolean;
    /**
     * Whether to hide the sort icon.
     */
    hideSortIcon?: boolean;
    /**
     * The icon to display in the cell, label is hidden.
     */
    icon?: IconName;
    /**
     * Access level of the cell.
     */
    accessLevel?: AccessLevelProp | null;
    /**
     * Whether the cell is visible only to Meter employees.
     */
    internal?: boolean;
    /**
     * Whether the cell is part of a nested `Table`.
     */
    isNested?: boolean;
    /**
     * Whether the cell is leading.
     */
    isLeading?: boolean;
    /**
     * The sort direction of the cell.
     */
    sortDirection?: 'asc' | 'desc' | boolean;
    /**
     * Whether the cell is stuck to the top, right, bottom, or left of the `Table`.
     */
    stuck?: 'top' | 'right' | 'bottom' | 'left';
    /**
     * Determines both the type and contents of the tooltip to display when the cell is hovered.
     */
    tooltip?: TableHeadCellTooltipProp;
    /**
     * Whether the cell has a field.
     */
    hasField?: boolean;
    /**
     * The group the cell belongs to.
     */
    group?: string | null;
    /**
     * Whether there is a collapsable cell before this cell (used for sticky positioning).
     */
    hasCollapsableBefore?: boolean;
  }
>;

function TableHeadCellInner(
  {
    alignment,
    children,
    collapsable,
    collapsed,
    hideLabel = false,
    hideSortIcon,
    icon,
    accessLevel,
    internal,
    isNested,
    isLeading,
    sortDirection,
    stuck,
    tooltip,
    hasField,
    group,
    hasCollapsableBefore,
    ...props
  }: TableHeadCellProps,
  ref: React.ForwardedRef<HTMLTableCellElement>,
) {
  const tableVariant = useTableVariant();
  const isMultiRowSelection = useTableMultiRowSelection();
  if (accessLevel === null) return null;

  const hasHeaderText = children != null && children !== false && children !== '';

  const tableHeadCellLabelContent = hideLabel ? (
    <TableHeadCellLabel visible={false}>{children}</TableHeadCellLabel>
  ) : (
    children
  );

  // `icon` stands in for the visible label, so the label still has to exist somewhere a reader can
  // get at it — otherwise the column heads its cells with a picture and nothing else.
  const tableHeadCellLabel = icon ? (
    <TableHeadCellIconLabel>
      <TableHeadCellIcon icon={icon} />
      {hasHeaderText && <TableHeadCellLabel visible={false}>{children}</TableHeadCellLabel>}
    </TableHeadCellIconLabel>
  ) : (
    tableHeadCellLabelContent
  );

  let tableHeadCellContents = tableHeadCellLabel;

  if (tooltip && tooltip.label) {
    tableHeadCellContents = (
      <LabelTooltip
        preset="small"
        weight="bold"
        internal={internal || accessLevel === AccessLevel.Internal}
        {...tooltip.label}
      >
        {tableHeadCellLabel}
      </LabelTooltip>
    );
  }

  if (tooltip && tooltip.icon) {
    tableHeadCellContents = (
      <IconTooltip
        size={16}
        internal={internal || accessLevel === AccessLevel.Internal}
        {...tooltip.icon}
      />
    );
  }

  const hasSort = !collapsable && (!hideSortIcon || !!sortDirection);

  // A head cell with nothing in it heads nothing. Readers announce the column header as they move
  // between cells, so an empty `<th>` leaves every cell in the column advertised as having a header
  // and unable to say what it is — better to not claim one. Head-row spacers already do this:
  // `TableCellBuffer` and `TableCellState` are `<td>` even with `head`. A `collapsable` cell is
  // excluded because it is itself the expand-all button, which is named below.
  const isEmpty = !collapsable && !tooltip && !hasHeaderText;

  // Nothing visible at all, like the head of an actions column. Pinned, it shouldn't hide or shade
  // what scrolls beneath it.
  const isBlank =
    !collapsable &&
    !tooltip &&
    !icon &&
    !sortDirection &&
    (hideLabel || !hasHeaderText) &&
    (!accessLevel || accessLevel === 'public');

  return (
    <TableHeadCellContainer
      as={isEmpty ? 'td' : undefined}
      data-blank={isBlank || undefined}
      collapsable={collapsable}
      isLeading={isLeading}
      isSorted={!!sortDirection}
      isMultiRowSelection={isMultiRowSelection}
      stuck={stuck}
      data-pinned={pinnedSide(isLeading, stuck)}
      hasCollapsableBefore={hasCollapsableBefore}
      {...props}
      ref={ref}
    >
      <TableHeadCellContents
        as={collapsable ? 'button' : 'div'}
        collapsable={collapsable}
        canSort={!hideSortIcon || !!sortDirection}
        tabIndex={collapsable || !hideSortIcon ? 0 : undefined}
        aria-expanded={collapsable ? !collapsed : undefined}
        alignment={alignment}
        internal={internal || accessLevel === AccessLevel.Internal}
        variant={tableVariant}
      >
        <AlignStack
          align="center"
          preset="small"
          width="min-content"
          start={
            collapsable && (
              <>
                <TableCellCollapsable icon={collapsed ? 'chevron-right' : 'chevron-down'} />
                {!hasHeaderText && <VisuallyHidden>Toggle all rows</VisuallyHidden>}
              </>
            )
          }
          end={
            (accessLevel && accessLevel !== 'public') || hasSort ? (
              <>
                {accessLevel && accessLevel !== 'public' && (
                  <TableHeadCellBadge>
                    <FeatureBadge size="x-small" type={accessLevel} />
                  </TableHeadCellBadge>
                )}
                {hasSort && (
                  <TableHeadCellSort isVisible={!!sortDirection}>
                    <TableHeadCellSortIcon
                      icon={sortDirection === 'asc' ? 'chevron-up' : 'chevron-down'}
                      size={10}
                    />
                  </TableHeadCellSort>
                )}
              </>
            ) : null
          }
        >
          {tableHeadCellContents}
        </AlignStack>
      </TableHeadCellContents>
    </TableHeadCellContainer>
  );
}

export const TableHeadCell = React.forwardRef(TableHeadCellInner);

const TableHeadRowContainer = styled('tr', {
  position: 'relative',
  color: colors.bodyNeutralLight,

  [darkThemeSelector]: {
    color: colors.bodyNeutralDark,
  },

  ...edgeCellCSS(
    TableHeadCellContainer.toString(),
    TableHeadCellContents.toString(),
    ZINDEX_HEAD_LEADING,
  ),
  ...pinnedCellCSS(TableHeadCellContents.toString()),

  // A blank pinned head cell (e.g. above an actions column) lets the labels scrolling beneath it
  // show through, and the gutter beside it too, keeping its fill only once it rests in its own slot
  // at the end.
  [`[data-scrolled-x-end="false"] > * > & > [data-blank][data-pinned] > ${TableHeadCellContents}`]:
    {
      backgroundColor: 'transparent',
    },
  [`[data-scrolled-x-end="false"] > * > &:has(> [data-blank][data-pinned="right"] + [data-side="trailing"]) > [data-side="trailing"]`]:
    {
      background: 'transparent',
    },
});
TableHeadRowContainer.displayName = 'TableHeadRowContainer';

type TableHeadRowProps = PropsWithChildren<HTMLAttributes<HTMLDivElement>>;

function TableHeadRowPassthrough(
  props: TableHeadRowProps,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const innerRef = useRef<HTMLDivElement>(null);
  const [isPinned, setIsPinned] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => setIsPinned(e.intersectionRatio < 1), {
      threshold: [1],
    });

    if (innerRef.current) {
      observer.observe(innerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <TableHeadRowContainer {...props} ref={mergeRefs([ref, innerRef])} data-is-pinned={isPinned} />
  );
}

export const TableHeadRow = React.forwardRef<HTMLDivElement, TableHeadCellProps>(
  TableHeadRowPassthrough,
);

export const TableHead = styled('thead');
TableHead.displayName = 'TableHead';

const TableContainer = styled('table', {
  position: 'relative',
  width: '100%',

  variants: {
    context: {
      application: {
        $$tableGutter: `$space$${sizing.primary}`,
      },
      content: {
        $$tableGutter: `$space$${sizing.tertiary}`,
      },
      section: {
        $$tableGutter: `$space$${sizing.tertiary}`,
      },
    },
    isNested: {
      true: {},
      false: {},
    },
    multiRowSelection: {
      true: {},
      false: {},
    },
  },
});
TableContainer.displayName = 'TableContainer';

interface TableProps extends PropsWithChildren<HTMLAttributes<HTMLDivElement>> {
  /**
   * The layout the table sits in, which sets the width of its leading and trailing gutters so
   * its first and last columns line up with the content around it.
   */
  context?: GutterContext;
  /**
   * Whether the table is nested within a parent `Table`.
   */
  isNested?: TableNestedProp;
  /**
   * Whether the table allows for multi-row selection.
   */
  multiRowSelection?: boolean;
  /**
   * The variant of table.
   */
  variant?: TableVariantProp;
}

export const Table = React.forwardRef<HTMLDivElement, TableProps>(
  ({ children, context = 'application', isNested, multiRowSelection, variant, ...props }, ref) => {
    const scroll = useCanScrollX();
    return (
      <TableContainer
        ref={mergeRefs([ref, scroll.ref])}
        context={context}
        isNested={isNested}
        data-can-scroll-x={scroll.canScrollX}
        data-scrolled-x-start={scroll.isScrolledToStart}
        data-scrolled-x-end={scroll.isScrolledToEnd}
        data-leading-gutter-stuck={scroll.isLeadingGutterStuck}
        data-pinned-shadow-left={scroll.pinnedShadowLeft || undefined}
        data-pinned-shadow-right={scroll.pinnedShadowRight || undefined}
        {...props}
      >
        <TableMultiRowSelectionProvider value={multiRowSelection}>
          <TableNestedProvider value={isNested}>
            <TableVariantProvider value={variant}>{children}</TableVariantProvider>
          </TableNestedProvider>
        </TableMultiRowSelectionProvider>
      </TableContainer>
    );
  },
);
